import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Spinner, Alert, Tab, Nav, Table, Form, Button, Badge, Pagination, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';

// 活躍用戶表格組件
const ActiveUsersTable = ({ users }) => {
  const [sortField, setSortField] = useState('activity_count');
  const [sortDirection, setSortDirection] = useState('desc');

  // 排序處理
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // 排序後的用戶列表
  const sortedUsers = [...users].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // 處理數字類型
    if (sortField === 'activity_count' || sortField === 'user_id') {
      aValue = parseInt(aValue) || 0;
      bValue = parseInt(bValue) || 0;
    }
    
    // 處理文字類型
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // 排序箭頭
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (!users || users.length === 0) {
    return <Alert variant="info">暫無活躍用戶資料</Alert>;
  }

  return (
    <Table responsive striped hover>
      <thead>
        <tr>
          <th 
            style={{ cursor: 'pointer' }} 
            onClick={() => handleSort('user_name')}
          >
            使用者 {getSortIcon('user_name')}
          </th>
          <th 
            style={{ cursor: 'pointer' }} 
            onClick={() => handleSort('store_name')}
          >
            社別 {getSortIcon('store_name')}
          </th>
          <th 
            style={{ cursor: 'pointer' }} 
            onClick={() => handleSort('activity_count')}
          >
            操作次數 {getSortIcon('activity_count')}
          </th>
          <th 
            style={{ cursor: 'pointer' }} 
            onClick={() => handleSort('last_activity_time')}
          >
            最後活動時間 {getSortIcon('last_activity_time')}
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedUsers.map((user, index) => (
          <tr key={user.user_id || index}>
            <td>
              <div className="d-flex align-items-center">
                {user.user_line_picture_url && (
                  <img
                    src={user.user_line_picture_url}
                    alt={user.user_name}
                    className="rounded-circle me-2"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'cover',
                      border: '1px solid #dee2e6'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <span>{user.user_name}</span>
              </div>
            </td>
            <td>{user.store_name || '未知社別'}</td>
            <td>
              <Badge variant="primary" className="fs-6">
                {user.activity_count}
              </Badge>
            </td>
            <td>
              <small className="text-muted">
                {user.last_activity_time ? 
                  new Date(user.last_activity_time).toLocaleString('zh-TW') : 
                  '-'
                }
              </small>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const SystemLogs = () => {
  const [userActivities, setUserActivities] = useState([]);
  const [secretaryModifications, setSecretaryModifications] = useState([]);
  const [systemStats, setSystemStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('activities');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    start_date: '',
    end_date: '',
    action_type: '',
    operation_type: '',
    table_name: ''
  });
  const [pagination, setPagination] = useState({});
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);
  const [activeUsersData, setActiveUsersData] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 組件載入時的調試資訊
  console.log('🏁 SystemLogs 組件載入:', {
    user,
    token: token ? `${token.substring(0, 20)}...` : null,
    fullToken: token,
    hasUser: !!user,
    userId: user?.e_id,
    userActivitiesLength: userActivities.length,
    systemStats,
    loading,
    error,
    activeTab
  });

  // 檢查權限
  useEffect(() => {
    const userId = user?.e_id;
    if (!user || !isSuperAdmin(userId)) {
      setError('權限不足，只有最高權限使用者可以查看系統日誌');
      setLoading(false);
      return;
    }
    fetchSystemStats();
  }, [user]);

  // 獲取系統統計
  const fetchSystemStats = async () => {
    try {
      console.log('🎯 發送系統統計請求:', {
        url: '/logs/system-stats',
        token: token ? `${token.substring(0, 20)}...` : null,
        hasToken: !!token
      });
      
      const response = await axios.get('/logs/system-stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('📊 系統統計回應:', response.data);
      setSystemStats(response.data);
    } catch (error) {
      console.error('❌ 獲取系統統計錯誤:', error);
      console.error('❌ 錯誤詳情:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
  };

  // 獲取使用者活動記錄
  const fetchUserActivities = async () => {
    console.log('🔍 開始獲取使用者活動記錄...', { filters, activeTab });
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      console.log('📡 發送請求:', `/logs/user-activities?${params}`, {
        token: token ? `${token.substring(0, 20)}...` : null,
        hasToken: !!token,
        headers: {
          'Authorization': token ? `Bearer ${token.substring(0, 20)}...` : null,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await axios.get(`/logs/user-activities?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ 收到回應:', response.data);
      console.log('📊 活動數量:', response.data.activities?.length);
      
      setUserActivities(response.data.activities || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (error) {
      console.error('❌ 獲取活動記錄錯誤:', error);
      setError('無法載入使用者活動記錄');
    }
    setLoading(false);
  };

  // 獲取秘書資料異動記錄
  const fetchSecretaryModifications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/logs/secretary-modifications?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setSecretaryModifications(response.data.modifications);
      setPagination(response.data.pagination);
      setError(null);
    } catch (error) {
      console.error('Error fetching secretary modifications:', error);
      setError('無法載入秘書資料異動記錄');
    }
    setLoading(false);
  };

  // 處理篩選器變更
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // 重置頁數
    }));
  };

  // 處理分頁變更
  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // 獲取活躍用戶詳細資料
  const fetchActiveUsersDetail = async (period) => {
    setModalLoading(true);
    try {
      const endpoint = period === 'today' ? 'today-active-users' : 'week-active-users';
      const title = period === 'today' ? '今日活躍用戶' : '本週活躍用戶';
      
      const response = await axios.get(`/logs/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setActiveUsersData(response.data.users || []);
      setModalTitle(title);
      setShowActiveUsersModal(true);
    } catch (error) {
      console.error('獲取活躍用戶詳細資料錯誤:', error);
      setError('無法載入活躍用戶詳細資料');
    }
    setModalLoading(false);
  };

  // 當標籤或篩選器變更時重新載入資料
  useEffect(() => {
    const userId = user?.e_id;
    console.log('🎯 useEffect 觸發:', { user: user?.e_name, userId, activeTab, filters });
    
    if (user && isSuperAdmin(userId)) {
      console.log('✅ 權限驗證通過，開始載入資料');
      if (activeTab === 'activities') {
        console.log('📋 載入使用者活動記錄');
        fetchUserActivities();
      } else if (activeTab === 'modifications') {
        console.log('📝 載入秘書資料異動記錄');
        fetchSecretaryModifications();
      }
    } else {
      console.log('❌ 權限不足或用戶未登入:', { userId, hasUser: !!user });
    }
  }, [filters, activeTab, user]);

  // 格式化日期時間
  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleString('zh-TW');
  };

  // 取得操作類型的徽章樣式
  const getActionBadgeVariant = (actionType) => {
    const variants = {
      'login': 'success',
      'logout': 'secondary',
      'view': 'info',
      'create': 'primary',
      'update': 'warning',
      'delete': 'danger'
    };
    return variants[actionType] || 'light';
  };

  const getOperationBadgeVariant = (operationType) => {
    const variants = {
      'INSERT': 'success',
      'UPDATE': 'warning',
      'DELETE': 'danger'
    };
    return variants[operationType] || 'light';
  };

  const userId = user?.e_id;
  if (error && user && !isSuperAdmin(userId)) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <h5>權限不足</h5>
          <p>只有最高權限使用者（e_id = 80）可以查看系統日誌。</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="text-center">系統日誌管理</h2>
          <p className="text-center text-muted">監控使用者活動和資料異動記錄</p>
        </Col>
      </Row>

      {/* 系統統計卡片 */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-primary">{systemStats.today?.today_activities || 0}</h4>
              <p className="mb-0">今日活動</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 
                className="text-success" 
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => fetchActiveUsersDetail('today')}
                title="點擊查看詳細"
              >
                {systemStats.today?.today_unique_users || 0}
              </h4>
              <p className="mb-0">今日活躍用戶</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-info">{systemStats.week?.week_activities || 0}</h4>
              <p className="mb-0">本週活動</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 
                className="text-warning" 
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => fetchActiveUsersDetail('week')}
                title="點擊查看詳細"
              >
                {systemStats.week?.week_unique_users || 0}
              </h4>
              <p className="mb-0">本週活躍用戶</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="activities">使用者活動記錄</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="modifications">資料異動記錄</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* 使用者活動記錄 */}
          <Tab.Pane eventKey="activities">
            <Card>
              <Card.Header>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>搜尋</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="姓名、API端點..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>操作類型</Form.Label>
                      <Form.Select
                        value={filters.action_type}
                        onChange={(e) => handleFilterChange('action_type', e.target.value)}
                      >
                        <option value="">全部</option>
                        <option value="login">登入</option>
                        <option value="logout">登出</option>
                        <option value="view">查看</option>
                        <option value="create">新增</option>
                        <option value="update">更新</option>
                        <option value="delete">刪除</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>開始日期</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>結束日期</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setFilters({
                        page: 1, limit: 20, search: '', start_date: '', 
                        end_date: '', action_type: '', operation_type: '', table_name: ''
                      })}
                    >
                      清除篩選
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center">
                    <Spinner animation="border" />
                    <p className="mt-2">載入中...</p>
                  </div>
                ) : (
                  <div>
                    <Table responsive striped hover>
                      <thead>
                        <tr>
                          <th>時間</th>
                          <th>使用者</th>
                          <th>社別</th>
                          <th>操作</th>
                          <th>描述</th>
                          <th>IP地址</th>
                          <th>狀態</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userActivities.map((activity) => (
                          <tr key={activity.id}>
                            <td>{formatDateTime(activity.created_at)}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {activity.user_line_picture_url && (
                                  <img
                                    src={activity.user_line_picture_url}
                                    alt={activity.user_name}
                                    className="rounded-circle me-2"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      objectFit: 'cover',
                                      border: '1px solid #dee2e6'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <span>{activity.user_name}</span>
                              </div>
                            </td>
                            <td>{activity.store_name}</td>
                            <td>
                              <Badge variant={getActionBadgeVariant(activity.action_type)}>
                                {activity.action_type}
                              </Badge>
                            </td>
                            <td>
                              <small>{activity.action_description}</small>
                            </td>
                            <td>{activity.ip_address}</td>
                            <td>
                              <Badge variant={activity.response_status === 200 ? 'success' : 'danger'}>
                                {activity.response_status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* 優化的分頁組件 */}
                    {pagination.total_pages > 1 && (
                      <div className="mt-4">
                        {/* 分頁資訊 */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="text-muted">
                            顯示第 {((pagination.current_page - 1) * pagination.per_page) + 1} - {Math.min(pagination.current_page * pagination.per_page, pagination.total)} 筆，
                            共 {pagination.total} 筆記錄
                          </div>
                          
                          {/* 每頁顯示數量選擇 */}
                          <div className="d-flex align-items-center">
                            <span className="text-muted me-2">每頁顯示：</span>
                            <Form.Select 
                              size="sm" 
                              style={{ width: '80px' }}
                              value={filters.limit}
                              onChange={(e) => handleFilterChange('limit', e.target.value)}
                            >
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </Form.Select>
                          </div>
                        </div>

                        {/* 分頁控制 */}
                        <div className="d-flex justify-content-center align-items-center flex-wrap">
                          <Pagination className="mb-0">
                            <Pagination.First 
                              disabled={pagination.current_page === 1}
                              onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev 
                              disabled={pagination.current_page === 1}
                              onClick={() => handlePageChange(pagination.current_page - 1)}
                            />
                            
                            {/* 智能分頁顯示 */}
                            {(() => {
                              const current = pagination.current_page;
                              const total = pagination.total_pages;
                              const pages = [];
                              
                              if (total <= 7) {
                                // 如果總頁數小於等於7，顯示所有頁數
                                for (let i = 1; i <= total; i++) {
                                  pages.push(
                                    <Pagination.Item
                                      key={i}
                                      active={i === current}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </Pagination.Item>
                                  );
                                }
                              } else {
                                // 複雜邏輯用於大量頁數
                                const delta = 2;
                                const rangeStart = Math.max(2, current - delta);
                                const rangeEnd = Math.min(total - 1, current + delta);
                                
                                pages.push(
                                  <Pagination.Item key={1} active={1 === current} onClick={() => handlePageChange(1)}>
                                    1
                                  </Pagination.Item>
                                );
                                
                                if (rangeStart > 2) {
                                  pages.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
                                }
                                
                                for (let i = rangeStart; i <= rangeEnd; i++) {
                                  pages.push(
                                    <Pagination.Item key={i} active={i === current} onClick={() => handlePageChange(i)}>
                                      {i}
                                    </Pagination.Item>
                                  );
                                }
                                
                                if (rangeEnd < total - 1) {
                                  pages.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
                                }
                                
                                if (total > 1) {
                                  pages.push(
                                    <Pagination.Item key={total} active={total === current} onClick={() => handlePageChange(total)}>
                                      {total}
                                    </Pagination.Item>
                                  );
                                }
                              }
                              
                              return pages;
                            })()}
                            
                            <Pagination.Next 
                              disabled={pagination.current_page === pagination.total_pages}
                              onClick={() => handlePageChange(pagination.current_page + 1)}
                            />
                            <Pagination.Last 
                              disabled={pagination.current_page === pagination.total_pages}
                              onClick={() => handlePageChange(pagination.total_pages)}
                            />
                          </Pagination>
                          
                          {/* 頁面跳轉 */}
                          <div className="ms-3 d-flex align-items-center">
                            <span className="text-muted me-2">跳至：</span>
                            <Form.Control
                              type="number"
                              size="sm"
                              style={{ width: '70px' }}
                              min="1"
                              max={pagination.total_pages}
                              defaultValue={pagination.current_page}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  const page = parseInt(e.target.value);
                                  if (page >= 1 && page <= pagination.total_pages) {
                                    handlePageChange(page);
                                  }
                                }
                              }}
                            />
                            <span className="text-muted ms-1">頁</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* 資料異動記錄 */}
          <Tab.Pane eventKey="modifications">
            <Card>
              <Card.Header>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>搜尋</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="操作者姓名、表名..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>操作類型</Form.Label>
                      <Form.Select
                        value={filters.operation_type}
                        onChange={(e) => handleFilterChange('operation_type', e.target.value)}
                      >
                        <option value="">全部</option>
                        <option value="INSERT">新增</option>
                        <option value="UPDATE">更新</option>
                        <option value="DELETE">刪除</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>資料表</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="表名"
                        value={filters.table_name}
                        onChange={(e) => handleFilterChange('table_name', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>開始日期</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>結束日期</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center">
                    <Spinner animation="border" />
                    <p className="mt-2">載入中...</p>
                  </div>
                ) : (
                  <div>
                    <Table responsive striped hover>
                      <thead>
                        <tr>
                          <th>時間</th>
                          <th>操作者</th>
                          <th>社別</th>
                          <th>操作</th>
                          <th>資料表</th>
                          <th>欄位</th>
                          <th>原值</th>
                          <th>新值</th>
                        </tr>
                      </thead>
                      <tbody>
                        {secretaryModifications.map((modification) => (
                          <tr key={modification.id}>
                            <td>{formatDateTime(modification.created_at)}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {modification.operator_line_picture_url && (
                                  <img
                                    src={modification.operator_line_picture_url}
                                    alt={modification.operator_name}
                                    className="rounded-circle me-2"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      objectFit: 'cover',
                                      border: '1px solid #dee2e6'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <span>{modification.operator_name}</span>
                              </div>
                            </td>
                            <td>{modification.operator_store_name}</td>
                            <td>
                              <Badge variant={getOperationBadgeVariant(modification.operation_type)}>
                                {modification.operation_type}
                              </Badge>
                            </td>
                            <td><code>{modification.table_name}</code></td>
                            <td><small>{modification.field_name}</small></td>
                            <td>
                              <small className="text-muted">
                                {modification.old_value ? 
                                  (modification.old_value.length > 50 ? 
                                    modification.old_value.substring(0, 50) + '...' : 
                                    modification.old_value
                                  ) : '-'
                                }
                              </small>
                            </td>
                            <td>
                              <small className="text-success">
                                {modification.new_value ? 
                                  (modification.new_value.length > 50 ? 
                                    modification.new_value.substring(0, 50) + '...' : 
                                    modification.new_value
                                  ) : '-'
                                }
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* 優化的分頁組件 */}
                    {pagination.total_pages > 1 && (
                      <div className="mt-4">
                        {/* 分頁資訊 */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="text-muted">
                            顯示第 {((pagination.current_page - 1) * pagination.per_page) + 1} - {Math.min(pagination.current_page * pagination.per_page, pagination.total)} 筆，
                            共 {pagination.total} 筆記錄
                          </div>
                          
                          {/* 每頁顯示數量選擇 */}
                          <div className="d-flex align-items-center">
                            <span className="text-muted me-2">每頁顯示：</span>
                            <Form.Select 
                              size="sm" 
                              style={{ width: '80px' }}
                              value={filters.limit}
                              onChange={(e) => handleFilterChange('limit', e.target.value)}
                            >
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </Form.Select>
                          </div>
                        </div>

                        {/* 分頁控制 */}
                        <div className="d-flex justify-content-center align-items-center flex-wrap">
                          <Pagination className="mb-0">
                            <Pagination.First 
                              disabled={pagination.current_page === 1}
                              onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev 
                              disabled={pagination.current_page === 1}
                              onClick={() => handlePageChange(pagination.current_page - 1)}
                            />
                            
                            {/* 智能分頁顯示 */}
                            {(() => {
                              const current = pagination.current_page;
                              const total = pagination.total_pages;
                              const pages = [];
                              
                              if (total <= 7) {
                                // 如果總頁數小於等於7，顯示所有頁數
                                for (let i = 1; i <= total; i++) {
                                  pages.push(
                                    <Pagination.Item
                                      key={i}
                                      active={i === current}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </Pagination.Item>
                                  );
                                }
                              } else {
                                // 複雜邏輯用於大量頁數
                                const delta = 2;
                                const rangeStart = Math.max(2, current - delta);
                                const rangeEnd = Math.min(total - 1, current + delta);
                                
                                pages.push(
                                  <Pagination.Item key={1} active={1 === current} onClick={() => handlePageChange(1)}>
                                    1
                                  </Pagination.Item>
                                );
                                
                                if (rangeStart > 2) {
                                  pages.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
                                }
                                
                                for (let i = rangeStart; i <= rangeEnd; i++) {
                                  pages.push(
                                    <Pagination.Item key={i} active={i === current} onClick={() => handlePageChange(i)}>
                                      {i}
                                    </Pagination.Item>
                                  );
                                }
                                
                                if (rangeEnd < total - 1) {
                                  pages.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
                                }
                                
                                if (total > 1) {
                                  pages.push(
                                    <Pagination.Item key={total} active={total === current} onClick={() => handlePageChange(total)}>
                                      {total}
                                    </Pagination.Item>
                                  );
                                }
                              }
                              
                              return pages;
                            })()}
                            
                            <Pagination.Next 
                              disabled={pagination.current_page === pagination.total_pages}
                              onClick={() => handlePageChange(pagination.current_page + 1)}
                            />
                            <Pagination.Last 
                              disabled={pagination.current_page === pagination.total_pages}
                              onClick={() => handlePageChange(pagination.total_pages)}
                            />
                          </Pagination>
                          
                          {/* 頁面跳轉 */}
                          <div className="ms-3 d-flex align-items-center">
                            <span className="text-muted me-2">跳至：</span>
                            <Form.Control
                              type="number"
                              size="sm"
                              style={{ width: '70px' }}
                              min="1"
                              max={pagination.total_pages}
                              defaultValue={pagination.current_page}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  const page = parseInt(e.target.value);
                                  if (page >= 1 && page <= pagination.total_pages) {
                                    handlePageChange(page);
                                  }
                                }
                              }}
                            />
                            <span className="text-muted ms-1">頁</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* 活躍用戶詳細資料 Modal */}
      <Modal show={showActiveUsersModal} onHide={() => setShowActiveUsersModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
              <p className="mt-2">載入中...</p>
            </div>
          ) : (
            <ActiveUsersTable users={activeUsersData} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActiveUsersModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SystemLogs;